import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type { Company } from '@/types/directory/Company';
import type { CompetencyMatrixReportParams } from '@/types/reports';

type FilterAttribute = { id?: string };

const toIds = (items: FilterAttribute[]): string[] =>
    items.map((item) => item.id).filter((id): id is string => !!id);

const ifNotEmpty = (ids: string[]): string[] | undefined => (ids.length > 0 ? ids : undefined);

export interface BuildReportFilterParamsInput {
    /** What the toolbar's company-attribute dropdown currently has selected. */
    filters: SelectedFilters;
    /** The current company, read for its `ageGroups`/`tenures` lists; `undefined` until loaded. */
    company: Company | undefined;
    /** The selected competency profile, or `undefined` for "all profiles". */
    competencyProfileId: string | undefined;
}

/**
 * `useCompanyAttributesFilters` only has dedicated buckets for departments, jobTitles,
 * companyAddresses, employeeLevels and managers (see its `standardKeys` lists) — every other
 * company attribute a user can pick, age group and tenure included, lands in the generic
 * `customAttributes` bucket. The report's own API contract, unlike that hook, has dedicated
 * `ageGroupIds`/`tenureIds` query params (Lot C spec, point 1), so this pure, report-local
 * function re-splits `customAttributes` by checking each selected id against the company's own
 * `ageGroups`/`tenures` lists.
 *
 * Chosen over extending the shared hook: `useCompanyAttributesFilters` and its `customAttributes`
 * bucket are read by several other pages (`DefineAudiencePage`, `CompetencyProfilesAdminPage`,
 * `SelectRevieweePage`, `DashboardPage`, …), every one of them mapping `customAttributes`
 * straight to their own `customAttributeIds`. Adding age group/tenure to the hook's
 * `standardKeys` would silently move those two attributes out of `customAttributes` for every
 * consumer that lets a user pick them today, with nothing reading the new buckets on their side —
 * a behaviour change this report has no business causing. A small function scoped to this report
 * carries the same fix with zero risk to the rest of the app.
 *
 * A genuine custom company attribute (`company.customAttributes`/`company.attributes`, neither an
 * age group nor a tenure) has no matching query param in this report's contract, so it is simply
 * dropped here — selecting one in the toolbar is a no-op on the query, a known, assumed gap (see
 * the Lot C deliverable).
 */
export const buildReportFilterParams = ({
    filters,
    company,
    competencyProfileId,
}: BuildReportFilterParamsInput): CompetencyMatrixReportParams => {
    const ageGroupIds = new Set((company?.ageGroups ?? []).map((ageGroup) => ageGroup.id));
    const tenureIds = new Set((company?.tenures ?? []).map((tenure) => tenure.id));

    const selectedAgeGroups = filters.customAttributes.filter(
        (attribute) => attribute.id && ageGroupIds.has(attribute.id),
    );
    const selectedTenures = filters.customAttributes.filter(
        (attribute) => attribute.id && tenureIds.has(attribute.id),
    );

    return {
        departmentIds: ifNotEmpty(toIds(filters.departments)),
        jobTitleIds: ifNotEmpty(toIds(filters.jobTitles)),
        companyAddressIds: ifNotEmpty(toIds(filters.companyAddresses)),
        employeeLevelIds: ifNotEmpty(toIds(filters.employeeLevels)),
        ageGroupIds: ifNotEmpty(toIds(selectedAgeGroups)),
        tenureIds: ifNotEmpty(toIds(selectedTenures)),
        managerIds: ifNotEmpty(toIds(filters.managers)),
        competencyProfileId,
    };
};
